import { getFeatureByPath } from '../../../micro_front_end/routing/services';
import { INSIGHTS_CARD_STATUS } from '../../../services/constants/status';
import { TCardInsight, TCardsContainer } from '../CardsContainer/types';
import {
  TCardInsightsPreference,
  TFilterPreference
} from '../FilterContainer/types';
import { TInsightsCards } from '../../../redux/userPreferences/types';
import { TTabID } from 'components/Insights/PublishTabs/types';
import { TFeatures } from '../../../redux/types';
import {
  TCardsInitialState,
  TInsightsInitialState
} from '../../../redux/settings/types';

export const changeHideStatus = (currentStatus: string): string => {
  switch (currentStatus) {
    case INSIGHTS_CARD_STATUS.HIDDEN:
      return INSIGHTS_CARD_STATUS.UNPUBLISHED;
    case INSIGHTS_CARD_STATUS.UNPUBLISHED:
    case INSIGHTS_CARD_STATUS.PUBLISHED:
    default:
      return INSIGHTS_CARD_STATUS.HIDDEN;
  }
};

export const changePublishStatus = (currentStatus: string): string => {
  switch (currentStatus) {
    case INSIGHTS_CARD_STATUS.PUBLISHED:
      return INSIGHTS_CARD_STATUS.UNPUBLISHED;
    case INSIGHTS_CARD_STATUS.HIDDEN:
    case INSIGHTS_CARD_STATUS.UNPUBLISHED:
    default:
      return INSIGHTS_CARD_STATUS.PUBLISHED;
  }
};

export const hasSameFiltersValues = (
  filterPreferences: TFilterPreference,
  card: TCardsContainer['card']
) => {
  return Object.keys(filterPreferences).every((filterKey: string) => {
    return (
      (filterPreferences as any)[filterKey] === (card as any)[filterKey] ||
      (filterPreferences as any)[filterKey] === 'all'
    );
  });
};

/**
 * Check if the current card exists in user settings or doesn't exists if the current status is unpublished
 * @param cardPreferences
 * @param card
 * @param status
 *
 * @return boolean
 */
export const findSameCardInPreferences = (
  cardPreferences: any,
  card: any,
  status: any
): boolean => {
  return (
    cardPreferences.find(
      (preference: TCardInsightsPreference) =>
        card.card.id === preference.id && status === preference.status
    ) ||
    (status === INSIGHTS_CARD_STATUS.UNPUBLISHED &&
      !cardPreferences.find(
        (preference: TCardInsightsPreference) => card.card.id === preference.id
      ))
  );
};

export const prepareByFilters = (
  cardList: TCardsContainer[],
  filterPreferences: TFilterPreference
) => {
  const cardsFiltered: TCardsContainer[] = [];
  if (Object.values(filterPreferences)?.length > 0) {
    cardList.forEach((cardInfos: TCardsContainer) => {
      const card: TCardsContainer['card'] = cardInfos.card;
      if (hasSameFiltersValues(filterPreferences, card)) {
        cardsFiltered.push(cardInfos);
      }
    });
    return cardsFiltered;
  }

  return cardList;
};

export const prepareByPreferences = (
  cardList: TCardsContainer[],
  cardPreferences: TCardsInitialState[],
  status: TTabID
) => {
  let cardsFiltered: TCardsContainer[] = [];
  if (cardPreferences?.length > 0 && cardList?.length > 0) {
    cardList?.forEach((card: TCardsContainer) => {
      if (findSameCardInPreferences(cardPreferences, card, status)) {
        cardsFiltered.push(card);
      }
    });
  } else {
    cardsFiltered =
      status === INSIGHTS_CARD_STATUS.UNPUBLISHED && cardList.length > 0
        ? [...cardList]
        : [];
  }

  return cardsFiltered;
};

export const checkPreferences = (
  currentTabPreferences?: TInsightsInitialState['current_tab'],
  features?: TFeatures
) => {
  let preference = {
    status: INSIGHTS_CARD_STATUS.PUBLISHED as TTabID
  };
  if (
    features &&
    getFeatureByPath(features, 'insights.edit') &&
    !!currentTabPreferences?.length
  ) {
    preference = { status: currentTabPreferences as TTabID } ?? {
      status: INSIGHTS_CARD_STATUS.UNPUBLISHED as TTabID
    };
  }
  return preference;
};

export const prepareFilterInsights = (
  cardList: TCardsContainer[],
  filterPreferences: TFilterPreference,
  cardPreferences: TCardsInitialState[]
): TCardsContainer[] => {
  const filters: any = { ...filterPreferences };
  delete filters.status;
  delete filters.current_account_id;
  const cardsFiltered = prepareByFilters(cardList, filters);
  return prepareByPreferences(
    cardsFiltered,
    cardPreferences,
    filterPreferences.status
  );
};

export const getCardPreferences = (
  cardId: string,
  cardPreferences?: TCardsInitialState[]
): TCardsInitialState | null => {
  return cardPreferences?.find((c) => c.id === cardId) ?? null;
};

export const prepareInsights = (
  cardList: TCardInsight[],
  cardPreferences?: TCardsInitialState[]
): TCardsContainer[] => {
  const list: TCardsContainer[] = [];
  if (!!cardList.length) {
    cardList?.forEach((card) => {
      const cardSettings = getCardPreferences(card.id, cardPreferences);
      list.push(<TCardsContainer>{
        cardSettings,
        card: {
          ...card
        }
      });
    });
  }
  return list;
};

export const removeAllHideCards = (
  cardsPreferences: TInsightsCards
): TInsightsCards => {
  const newStatus = cardsPreferences.status.filter(
    (card: TCardInsightsPreference) =>
      card.status !== INSIGHTS_CARD_STATUS.HIDDEN
  );
  return {
    ...cardsPreferences,
    status: newStatus
  };
};
